import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Space,
  Tag,
  Tooltip,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Progress,
  Divider,
  Typography,
  Alert,
  Tabs,
  InputNumber,
  Slider,
  Switch,
  Spin,
  List,
  Drawer,
  Breadcrumb,
  Segmented,
  Checkbox,
  Collapse,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  LinkOutlined,
  FileExcelOutlined,
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  TableOutlined,
  AppstoreOutlined,
  FileAddOutlined,
  SaveOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { v2ToppingsApi, api, gamificationApi } from '../../../../api';
import { getUserProfile } from '../../../../auth';
import AddToppingWizard from './AddToppingWizard';
import EditToppingWizard from './EditToppingWizard';
import TipsRotator from '../../../Common/TipsRotator';
import { generalTips } from '../../../Common/tipsData';
import { LockOutlined } from '@ant-design/icons';
import EnhancedTour, { TourHelpButton } from '../../../Common/EnhancedTour';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Dragger } = Upload;

const ToppingManager = () => {
  // 状態管理
  const [toppings, setToppings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingTopping, setEditingTopping] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingTopping, setViewingTopping] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [stats, setStats] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploadType, setUploadType] = useState('file');
  const [processingStatus, setProcessingStatus] = useState({});
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 表示形式

  // 一括編集用の状態
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchEditModalVisible, setBatchEditModalVisible] = useState(false);
  const [batchEditForm] = Form.useForm();
  const [batchEditLoading, setBatchEditLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);

  // 新しいウィザード用の状態
  const [wizardVisible, setWizardVisible] = useState(false);
  const [editWizardVisible, setEditWizardVisible] = useState(false);
  const [editingToppingForWizard, setEditingToppingForWizard] = useState(null);

  // ユーザーフィルター用の状態
  const [userRole, setUserRole] = useState('user');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // ツアー管理（独自情報管理用）
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep,
    updateCurrentStep,
    loading: tourLoading,
  } = useTutorialTour('topping_management');

  // 機能制御用の状態
  const [featureStatus, setFeatureStatus] = useState({
    unlocked: false,
    currentCount: 0,
    currentUnits: 0,
    maxLimit: 0,
    requiredPlan: '',
  });

  // ユニット使用状況
  const [usageData, setUsageData] = useState({
    current_units: 0,
    max_units: 0,
    current_count: 0,
    storage_mb: 0,
    max_storage_mb: 0,
    items: [],
  });

  // 独自情報一覧取得
  const fetchToppings = async (params = {}) => {
    setLoading(true);
    try {
      // 管理者の場合、選択されたユーザーIDをパラメータに追加
      if ((userRole === 'admin' || userRole === 'master') && selectedUserId) {
        params.user_id = selectedUserId;
      }
      console.log(
        '[ToppingManagement] Fetching with params:',
        params,
        'userRole:',
        userRole,
        'selectedUserId:',
        selectedUserId,
      );
      const response = await v2ToppingsApi.getToppings(params);
      console.log('[ToppingManagement] Response count:', response.data?.length);
      // バックエンドは直接配列を返すため、response.dataを使用
      const toppingsData = response.data || [];
      console.log('Fetched toppings raw:', response.data); // デバッグログ
      console.log('First topping detail:', toppingsData[0]); // 最初のアイテムの詳細

      // URLタイプのトッピングのデバッグ
      const urlToppings = toppingsData.filter(t => t.type === 'url');
      console.log('URL toppings:', urlToppings);
      urlToppings.forEach(topping => {
        console.log(`Topping ID ${topping.id}:`, {
          source_url: topping.source_url,
          raw_data: topping.raw_data,
          url: topping.url,
          title: topping.title,
        });
      });

      setToppings(toppingsData);

      // 統計情報とユニット使用状況を同時に取得（エラーが発生してもメイン処理は続行）
      try {
        const statsResponse = await v2ToppingsApi.getToppingsStats();
        setStats(statsResponse.data);
      } catch (statsError) {
        console.warn('統計情報の取得に失敗しました:', statsError);
      }

      // ユニット使用状況を取得
      try {
        const usageResponse = await v2ToppingsApi.getToppingUsage();
        setUsageData(usageResponse.data || {});
      } catch (usageError) {
        console.warn('ユニット使用状況の取得に失敗しました:', usageError);
      }
    } catch (error) {
      message.error('独自情報の取得に失敗しました');
      console.error('Error fetching toppings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const profile = await getUserProfile();
        setUserRole(profile.role);

        // 管理者の場合、ユーザー一覧を取得
        if (profile.role === 'admin' || profile.role === 'master') {
          const response = await api.get('/users/', {
            params: { page_size: 1000 },
          });
          setUsers(response.data?.users || []);
        }
      } catch (e) {
        console.error('Failed to get user profile:', e);
      }
    };

    // 機能制御状態を取得
    const fetchFeatureStatus = async () => {
      try {
        const response = await gamificationApi.getFeaturesStatus();
        // topping_data または topping_units を参照
        const toppingStatus =
          response.features?.topping_data ||
          response.features?.topping_units ||
          response.features?.topping ||
          {};
        const planDisplayNames = { lite: 'ライト', standard: 'スタンダード', premium: 'プレミアム' };
        setFeatureStatus({
          unlocked: toppingStatus.unlocked || false,
          currentCount: toppingStatus.current_count || 0,
          currentUnits: toppingStatus.current_units || 0,
          maxLimit: toppingStatus.max_limit || 0,
          requiredPlan: planDisplayNames[toppingStatus.required_plan] || toppingStatus.required_plan || '',
        });
      } catch (e) {
        console.error('Failed to get feature status:', e);
      }
    };

    fetchUserInfo();
    fetchFeatureStatus();
    fetchToppings();
  }, []);

  // ユーザーフィルター変更時に再読み込み
  useEffect(() => {
    if (userRole) {
      fetchToppings();
    }
  }, [selectedUserId]);

  // ファイルアップロード処理
  const handleUpload = async options => {
    const { file, onSuccess, onError } = options;

    try {
      const formData = new FormData();
      formData.append('file', file);

      // タイトルの取得とデフォルト値設定
      const title =
        form.getFieldValue('title') || file.name.split('.')[0] || 'Untitled';
      formData.append('title', title);

      formData.append('description', form.getFieldValue('description') || '');
      formData.append('category', form.getFieldValue('category') || '');
      formData.append('tags', JSON.stringify(form.getFieldValue('tags') || []));
      formData.append(
        'rag_enabled',
        form.getFieldValue('rag_enabled') !== false,
      );
      formData.append('chunk_size', form.getFieldValue('chunk_size') || 1000);
      formData.append(
        'chunk_overlap',
        form.getFieldValue('chunk_overlap') || 200,
      );

      console.log('Uploading file with title:', title); // デバッグログ

      const response = await v2ToppingsApi.uploadTopping(formData);

      message.success('ファイルのアップロードに成功しました');
      onSuccess(response.data);

      // 処理状況を監視
      checkProcessingStatus(response.data.id);
    } catch (error) {
      console.error('Upload error:', error);
      message.error('ファイルのアップロードに失敗しました');
      onError(error);
    }
  };

  // 処理状況チェック
  const checkProcessingStatus = async toppingId => {
    const checkInterval = setInterval(async () => {
      try {
        const response = await v2ToppingsApi.getTopping(toppingId);
        const topping = response.data;

        setProcessingStatus(prev => ({
          ...prev,
          [toppingId]: topping.status,
        }));

        if (topping.status === 'completed' || topping.status === 'error') {
          clearInterval(checkInterval);
          fetchToppings(); // リストを更新
        }
      } catch (error) {
        clearInterval(checkInterval);
      }
    }, 2000); // 2秒ごとにチェック
  };

  // モーダル表示
  const showDrawer = (topping = null) => {
    // 編集の場合は新しいウィザードを使用
    if (topping) {
      setEditingToppingForWizard(topping);
      setEditWizardVisible(true);
      return;
    }

    // 新規追加の場合は従来のドロワーを使用（廃止予定）
    setEditingTopping(topping);
    form.resetFields();
    setUploadType('file');
    setDrawerVisible(true);
  };

  // フォーム送信
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingTopping) {
        // 更新
        await v2ToppingsApi.updateTopping(editingTopping.id, values);
        message.success('独自情報を更新しました');
      } else if (uploadType === 'url') {
        // URL追加
        const response = await v2ToppingsApi.createToppingFromUrl({
          ...values,
          type: 'url',
          raw_data: values.url,
        });
        message.success('URLから独自情報を追加しました');
        checkProcessingStatus(response.data.id);
      } else if (uploadType === 'text') {
        // テキスト追加
        await v2ToppingsApi.createTopping({
          ...values,
          type: 'text',
          content: values.content,
        });
        message.success('テキスト独自情報を追加しました');
      }

      setDrawerVisible(false);
      fetchToppings();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // 削除処理
  const handleDelete = async id => {
    try {
      await v2ToppingsApi.deleteTopping(id);
      message.success('独自情報を削除しました');
      fetchToppings();
    } catch (error) {
      message.error('削除に失敗しました');
    }
  };

  // 詳細表示
  const showViewModal = async topping => {
    setViewingTopping(topping);
    setViewModalVisible(true);
  };

  // アイコン取得
  const getTypeIcon = type => {
    switch (type) {
      case 'csv':
        return <FileExcelOutlined style={{ color: '#52c41a' }} />;
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
      case 'url':
        return <LinkOutlined style={{ color: '#1890ff' }} />;
      case 'text':
        return <FileTextOutlined style={{ color: '#722ed1' }} />;
      case 'html':
        return <FileTextOutlined style={{ color: '#fa8c16' }} />;
      default:
        return <FileTextOutlined />;
    }
  };

  // ステータスタグ
  const getStatusTag = (status, record) => {
    // is_activeフィールドを優先的に使用
    if (record && typeof record.is_active === 'boolean') {
      return record.is_active ? (
        <Tag color="green">アクティブ</Tag>
      ) : (
        <Tag color="red">無効</Tag>
      );
    }

    // フォールバック: 従来のステータス表示
    const statusConfig = {
      pending: { color: 'orange', text: '処理待ち' },
      processing: { color: 'blue', text: '処理中' },
      completed: { color: 'green', text: 'アクティブ' },
      error: { color: 'red', text: 'エラー' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // テーブルカラム定義
  const columns = [
    {
      title: 'タイプ',
      dataIndex: 'type',
      key: 'type',
      width: 90, // 10px広げる
      align: 'center',
      render: type => getTypeIcon(type),
      filters: [
        { text: 'CSV', value: 'csv' },
        { text: 'PDF', value: 'pdf' },
        { text: 'URL', value: 'url' },
        { text: 'テキスト', value: 'text' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="タイトルで検索"
            value={selectedKeys[0]}
            onChange={e =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              検索
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              リセット
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.title.toString().toLowerCase().includes(value.toLowerCase()),
      render: text => <Text>{text}</Text>,
    },
    {
      title: 'カテゴリー',
      dataIndex: 'category',
      key: 'category',
      width: 184, // 64px広げる (120 + 64)
      filters: [
        { text: 'CTA・行動喚起', value: 'cta' },
        { text: '商品・サービス情報', value: 'product' },
        { text: '事例・実績', value: 'case_study' },
        { text: 'よくある質問', value: 'faq' },
        { text: '統計・データ', value: 'statistics' },
        { text: '口コミ・評価', value: 'testimonial' },
        { text: '会社情報', value: 'company' },
        { text: 'サポート・問い合わせ', value: 'support' },
        { text: 'その他', value: 'other' },
      ],
      onFilter: (value, record) => record.category === value,
      render: (category, record) => {
        console.log(
          'Rendering category:',
          category,
          'for record:',
          record.id,
          record.title,
        );
        const categoryMap = {
          'cta': 'CTA・行動喚起',
          'product': '商品・サービス情報',
          'case_study': '事例・実績',
          'faq': 'よくある質問',
          'statistics': '統計・データ',
          'testimonial': '口コミ・評価',
          'company': '会社情報',
          'support': 'サポート・問い合わせ',
          'other': 'その他',
        };
        const displayName = category ? categoryMap[category] || category : null;
        return displayName ? (
          <Tag color="purple">{displayName}</Tag>
        ) : (
          <Text type="secondary">-</Text>
        );
      },
    },
    {
      title: 'タグ',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      filters: (() => {
        // 全てのタグを収集してユニークなフィルターオプションを作成
        const allTags = new Set();
        toppings.forEach(topping => {
          if (topping.tags && Array.isArray(topping.tags)) {
            topping.tags.forEach(tag => allTags.add(tag));
          }
        });
        return Array.from(allTags).map(tag => ({ text: tag, value: tag }));
      })(),
      onFilter: (value, record) => {
        return (
          record.tags &&
          Array.isArray(record.tags) &&
          record.tags.includes(value)
        );
      },
      render: (tags, record) => {
        console.log(
          'Rendering tags:',
          tags,
          'for record:',
          record.id,
          record.title,
        );
        return (
          <div>
            {tags && Array.isArray(tags) && tags.length > 0 ? (
              tags.map((tag, index) => (
                <Tag key={tag} color="volcano" style={{ marginBottom: 2 }}>
                  {tag}
                </Tag>
              ))
            ) : (
              <Text type="secondary">-</Text>
            )}
          </div>
        );
      },
    },
    {
      title: '有効化',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => {
        console.log(
          'Rendering status:',
          status,
          'is_active:',
          record.is_active,
          'for record:',
          record.id,
          record.title,
        );
        return (
          <div onClick={e => e.stopPropagation()}>
            {' '}
            {/* 行クリックイベントを無効化 */}
            <Switch
              size="small"
              checked={record.is_active}
              onChange={async checked => {
                try {
                  await v2ToppingsApi.updateTopping(record.id, {
                    is_active: checked,
                  });
                  // ローカル状態を更新
                  setToppings(prev =>
                    prev.map(item =>
                      item.id === record.id
                        ? { ...item, is_active: checked }
                        : item,
                    ),
                  );
                  message.success(
                    `${record.title}を${checked ? '有効化' : '無効化'}しました`,
                  );
                } catch (error) {
                  message.error('ステータスの更新に失敗しました');
                }
              }}
            />
          </div>
        );
      },
    },
    {
      title: (
        <Tooltip title="100行(CSV)/5ページ(PDF)/5000文字(TEXT)ごとに1ユニット消費">
          <span style={{ cursor: 'help' }}>ユニット</span>
        </Tooltip>
      ),
      dataIndex: 'content',
      key: 'units',
      width: 120,
      sorter: (a, b) => {
        const calcUnits = record => {
          const content = record.content || '';
          if (record.type === 'csv') {
            // 様々なソースから行数を取得（row_count優先）
            const rowCount =
              record.structured_data?.row_count ||
              record.structured_data?.total_rows ||
              content.split('\n').length;
            return rowCount > 0 ? Math.ceil(rowCount / 100) : 1;
          } else if (record.type === 'pdf') {
            const pageCount =
              record.structured_data?.page_count ||
              record.structured_data?.pages ||
              0;
            return pageCount > 0 ? Math.ceil(pageCount / 5) : 1;
          } else if (record.type === 'url') {
            return 1;
          } else {
            const charCount = content.length;
            return charCount > 0 ? Math.ceil(charCount / 5000) : 1;
          }
        };
        return calcUnits(a) - calcUnits(b);
      },
      render: (content, record) => {
        // ユニット計算（バックエンドと同じロジック：切り上げ）
        const calculateUnits = () => {
          if (record.type === 'csv') {
            // 様々なソースから行数を取得（row_count優先）
            const rowCount =
              record.structured_data?.row_count ||
              record.structured_data?.total_rows ||
              (content || '').split('\n').length;
            return rowCount > 0 ? Math.ceil(rowCount / 100) : 1;
          } else if (record.type === 'pdf') {
            const pageCount =
              record.structured_data?.page_count ||
              record.structured_data?.pages ||
              0;
            return pageCount > 0 ? Math.ceil(pageCount / 5) : 1;
          } else if (record.type === 'url') {
            return 1;
          } else {
            const charCount = (content || '').length;
            return charCount > 0 ? Math.ceil(charCount / 5000) : 1;
          }
        };

        const units = calculateUnits();
        const getUnitDetails = () => {
          if (record.type === 'csv') {
            const rowCount =
              record.structured_data?.row_count ||
              record.structured_data?.total_rows ||
              (content || '').split('\n').length;
            return `${rowCount.toLocaleString()}行`;
          } else if (record.type === 'pdf') {
            const pageCount =
              record.structured_data?.page_count ||
              record.structured_data?.pages ||
              0;
            return `${pageCount}ページ`;
          } else if (record.type === 'url') {
            return 'URL';
          } else {
            const charCount = (content || '').length;
            return `${charCount.toLocaleString()}文字`;
          }
        };

        return (
          <Tooltip title={getUnitDetails()}>
            <Text strong style={{ color: '#1890ff' }}>
              {units}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: '最終更新日',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 128, // 48px広げる (80 + 48)
      render: date => new Date(date || new Date()).toLocaleDateString('ja-JP'),
      sorter: (a, b) =>
        new Date(a.updated_at || a.created_at) -
        new Date(b.updated_at || b.created_at),
    },
    {
      title: 'アクション',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space onClick={e => e.stopPropagation()}>
          {' '}
          {/* 行クリックイベントを無効化 */}
          <Tooltip title="詳細">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => showViewModal(record)}
            />
          </Tooltip>
          <Tooltip title="編集">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setEditingToppingForWizard(record);
                setEditWizardVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="この独自情報を削除しますか？"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Tooltip title="削除">
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 高度な検索機能
  const handleAdvancedSearch = async values => {
    setSearchLoading(true);
    try {
      // パラメータを清理（空の値を除去）
      const params = {
        q: values.query,
        search_type: values.searchType || 'all',
        skip: 0,
        limit: 50,
      };

      // 空でない値のみ追加
      if (values.category) params.category = values.category;
      if (values.tags && values.tags.length > 0) params.tags = values.tags;
      if (values.status) params.status = values.status;

      console.log('Search params:', params);

      const response = await v2ToppingsApi.searchToppings(params);

      setSearchResults(response.data);
      message.success(`${response.data.total}件の結果が見つかりました`);
    } catch (error) {
      message.error('検索に失敗しました');
      console.error('Search error:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setSearchLoading(false);
    }
  };

  // ハイライト表示
  const renderHighlightedText = (text, highlighted) => {
    if (!highlighted) return text;

    return (
      <span
        dangerouslySetInnerHTML={{
          __html: highlighted.replace(
            /<mark>/g,
            '<mark style="background-color: yellow;">',
          ),
        }}
      />
    );
  };

  // カードビュー用のレンダリング
  const renderCardView = () => (
    <Row gutter={[16, 16]}>
      {toppings.map(record => (
        <Col xs={24} sm={12} md={8} lg={6} key={record.id}>
          <Card
            hoverable
            size="small"
            style={{
              height: '280px',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={() => showViewModal(record)}
            title={
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {getTypeIcon(record.type)}
                <Text strong ellipsis style={{ flex: 1 }}>
                  {record.title}
                </Text>
              </div>
            }
            extra={
              <Space onClick={e => e.stopPropagation()}>
                {' '}
                {/* 行クリックイベントを無効化 */}
                <Tooltip title="編集">
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => {
                      setEditingToppingForWizard(record);
                      setEditWizardVisible(true);
                    }}
                  />
                </Tooltip>
                <Popconfirm
                  title="削除しますか？"
                  onConfirm={() => handleDelete(record.id)}
                  okText="削除"
                  cancelText="キャンセル"
                >
                  <Tooltip title="削除">
                    <Button icon={<DeleteOutlined />} size="small" danger />
                  </Tooltip>
                </Popconfirm>
              </Space>
            }
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                minHeight: '80px',
              }}
            >
              {/* ステータス・カテゴリー・タグのメタ情報 */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  minHeight: '80px',
                }}
              >
                {/* ステータス行 */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Text
                    type="secondary"
                    style={{ fontSize: '12px', minWidth: '50px' }}
                  >
                    ステータス:
                  </Text>
                  {getStatusTag(record.status, record)}
                </div>

                {/* カテゴリー行 */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Text
                    type="secondary"
                    style={{ fontSize: '12px', minWidth: '50px' }}
                  >
                    カテゴリー:
                  </Text>
                  {record.category ? (
                    (() => {
                      const categoryMap = {
                        'cta': 'CTA・行動喚起',
                        'product': '商品・サービス情報',
                        'case_study': '事例・実績',
                        'faq': 'よくある質問',
                        'statistics': '統計・データ',
                        'testimonial': '口コミ・評価',
                        'company': '会社情報',
                        'support': 'サポート・問い合わせ',
                        'other': 'その他',
                      };
                      const displayName =
                        categoryMap[record.category] || record.category;
                      return (
                        <Tag color="purple" style={{ margin: 0 }}>
                          {displayName}
                        </Tag>
                      );
                    })()
                  ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      -
                    </Text>
                  )}
                </div>

                {/* タグ行 */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <Text
                    type="secondary"
                    style={{
                      fontSize: '12px',
                      minWidth: '50px',
                      lineHeight: '22px',
                    }}
                  >
                    タグ:
                  </Text>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                      maxHeight: '44px',
                      overflow: 'hidden',
                    }}
                  >
                    {record.tags &&
                    Array.isArray(record.tags) &&
                    record.tags.length > 0 ? (
                      <>
                        {record.tags.slice(0, 3).map((tag, index) => (
                          <Tag
                            key={tag}
                            color="volcano"
                            style={{ margin: 0, fontSize: '11px' }}
                          >
                            {tag}
                          </Tag>
                        ))}
                        {record.tags.length > 3 && (
                          <Tooltip title={record.tags.slice(3).join(', ')}>
                            <Tag
                              color="volcano"
                              style={{ margin: 0, fontSize: '11px' }}
                            >
                              +{record.tags.length - 3}
                            </Tag>
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        -
                      </Text>
                    )}
                  </div>
                </div>
              </div>

              {/* 説明文 */}
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  説明:
                </Text>
                <div style={{ marginTop: '4px' }}>
                  <Text style={{ fontSize: '13px' }}>
                    {record.description
                      ? record.description.length > 80
                        ? `${record.description.substring(0, 80)}...`
                        : record.description
                      : 'なし'}
                  </Text>
                </div>
              </div>

              {/* フッター情報 */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '8px',
                  borderTop: '1px solid #f0f0f0',
                }}
              >
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {new Date(
                    record.updated_at || record.created_at,
                  ).toLocaleDateString('ja-JP')}
                </Text>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {record.type === 'csv'
                    ? `${Math.max(0, record.content.split('\n').filter(line => line.trim() !== '').length - 1).toLocaleString()}件`
                    : `${record.content.length.toLocaleString()}文字`}
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );

  // タグ取得
  const fetchAvailableTags = async () => {
    try {
      const response = await v2ToppingsApi.getTags();
      setAvailableTags(response.data?.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  // 一括編集処理
  const handleBatchEdit = async values => {
    try {
      setBatchEditLoading(true);

      const updates = {};
      if (values.status !== undefined) {
        updates.is_active = values.status;
      }
      if (values.category !== undefined && values.category !== '') {
        updates.category = values.category;
      }
      if (values.tags && values.tags.length > 0) {
        updates.tags = values.tags;
      }

      // 選択されたアイテムを一括更新
      const updatePromises = selectedRowKeys.map(id =>
        v2ToppingsApi.updateTopping(id, updates),
      );

      await Promise.all(updatePromises);

      message.success(`${selectedRowKeys.length}件の独自情報を更新しました`);
      setBatchEditModalVisible(false);
      setSelectedRowKeys([]);
      batchEditForm.resetFields();
      fetchToppings(); // リストを再取得
    } catch (error) {
      console.error('Batch edit error:', error);
      message.error('一括編集に失敗しました');
    } finally {
      setBatchEditLoading(false);
    }
  };

  // 一括削除処理
  const handleBatchDelete = async () => {
    try {
      setBatchEditLoading(true);

      const deletePromises = selectedRowKeys.map(id =>
        v2ToppingsApi.deleteTopping(id),
      );

      await Promise.all(deletePromises);

      message.success(`${selectedRowKeys.length}件の独自情報を削除しました`);
      setSelectedRowKeys([]);
      fetchToppings(); // リストを再取得
    } catch (error) {
      console.error('Batch delete error:', error);
      message.error('一括削除に失敗しました');
    } finally {
      setBatchEditLoading(false);
    }
  };

  // 行選択設定
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: () => ({
      // すべての行で選択可能
    }),
  };

  // ツアーステップを生成（target関数はEnhancedTour内で動的に評価される）
  const tourSteps = React.useMemo(() => {
    if (tourLoading || !TOUR_CONFIGS.topping_management) return [];
    return TOUR_CONFIGS.topping_management.steps;
  }, [tourLoading]);

  // ツアーステップ変更時のアクション処理
  const handleTourStepChange = (newStep) => {
    // 現在のステップのアクションを確認（次のステップに進む前に実行）
    const prevStepConfig = TOUR_CONFIGS.topping_management?.steps[currentStep];
    
    // 前のステップにopen_create_modalアクションがある場合、ウィザードを開く
    if (prevStepConfig?.action === 'open_create_modal' && newStep > currentStep) {
      // ウィザードを開いてから少し待ってステップを進める
      setWizardVisible(true);
      setTimeout(() => {
        updateCurrentStep(newStep);
      }, 400);
      return;
    }
    
    // 前のステップにclose_modalアクションがある場合
    if (prevStepConfig?.action === 'close_modal' && newStep > currentStep) {
      // ウィザードを閉じてから少し待ってステップを進める
      setWizardVisible(false);
      setTimeout(() => {
        updateCurrentStep(newStep);
      }, 400);
      return;
    }
    
    // 通常のステップ変更
    updateCurrentStep(newStep);
  };

  return (
    <div>
      {/* ツアー */}
      {tourSteps.length > 0 && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => {
            setWizardVisible(false);
            completeTour();
          }}
          current={currentStep}
          onChange={handleTourStepChange}
          onSkip={() => {
            setWizardVisible(false);
            skipTour();
          }}
          allowSkip={true}
          tourTitle="独自情報管理ガイド"
          mask={true}
        />
      )}

      {/* ヘッダー部分 - パンくず追加/統一デザイン */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/aio/dashboard">AIOコンテンツモード</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>独自情報管理</Breadcrumb.Item>
        </Breadcrumb>
        {(featureStatus.unlocked || userRole === 'admin' || userRole === 'master') && (
          <TourHelpButton
            onClick={restartTour}
            tooltip="ガイドツアーを再表示"
          />
        )}
      </div>
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title
              level={1}
              className="gradient-text"
              style={{ fontSize: '24px' }}
            >
              独自情報管理
            </Title>
            <Text type="secondary">
              記事生成に使用する独自情報（CSV、PDF、URL、テキスト）を管理します
            </Text>
          </Col>
          {/* ユニット・容量使用状況 */}
          {(featureStatus.unlocked ||
            userRole === 'admin' ||
            userRole === 'master') && (
            <Col>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                  padding: '12px 20px',
                  background:
                    usageData.units_exceeded || usageData.storage_exceeded
                      ? 'linear-gradient(135deg, #fff2f0 0%, #ffeded 100%)'
                      : 'linear-gradient(135deg, #f5f0ff 0%, #efe6ff 100%)',
                  borderRadius: 10,
                  border:
                    usageData.units_exceeded || usageData.storage_exceeded
                      ? '1px solid #ffccc7'
                      : '1px solid #d3c4e3',
                }}
              >
                {/* ユニット使用状況 */}
                <Tooltip
                  title={
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        ユニット使用量
                        {usageData.units_exceeded && (
                          <span style={{ color: '#ff4d4f', marginLeft: 8 }}>
                            ⚠ 超過中
                          </span>
                        )}
                      </div>
                      <div>
                        <ul>
                          <li>URL : 1URL / 1U</li>
                          <li>CSV : 100行 / 1U</li>
                          <li>PDF : 5ページ / 1U</li>
                          <li>TEXT : 5000文字 / 1U</li>
                        </ul>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        現在の登録件数: {usageData.current_count || 0}件
                      </div>
                      {usageData.units_exceeded && (
                        <div style={{ marginTop: 8, color: '#ff4d4f' }}>
                          上限を超えているため新規登録できません。
                          不要な独自情報を削除するか、ランクアップしてください。
                        </div>
                      )}
                    </div>
                  }
                >
                  <div style={{ minWidth: 140, cursor: 'help' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: usageData.units_exceeded
                            ? '#ff4d4f'
                            : '#595959',
                          fontWeight: usageData.units_exceeded
                            ? 'bold'
                            : 'normal',
                        }}
                      >
                        ユニット {usageData.units_exceeded && '⚠'}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          color: usageData.units_exceeded
                            ? '#ff4d4f'
                            : '#8c8c8c',
                        }}
                      >
                        {usageData.current_units || 0} /{' '}
                        {usageData.max_units || 0}
                      </span>
                    </div>
                    <Progress
                      percent={
                        usageData.max_units > 0
                          ? Math.min(
                              100,
                              Math.round(
                                (usageData.current_units /
                                  usageData.max_units) *
                                  100,
                              ),
                            )
                          : 0
                      }
                      size="small"
                      strokeColor={
                        usageData.units_exceeded
                          ? '#ff4d4f'
                          : {
                              '0%': '#722ed1',
                              '100%': '#b37feb',
                            }
                      }
                      showInfo={false}
                    />
                  </div>
                </Tooltip>

                {/* 容量使用状況 */}
                <Tooltip
                  title={
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        ストレージ容量
                        {usageData.storage_exceeded && (
                          <span style={{ color: '#ff4d4f', marginLeft: 8 }}>
                            ⚠ 超過中
                          </span>
                        )}
                      </div>
                      <div>アップロードしたファイルの合計容量です</div>
                      <div style={{ marginTop: 4 }}>
                        ※1ファイルあたりは最大10MBまで
                      </div>
                      {usageData.storage_exceeded && (
                        <div style={{ marginTop: 8, color: '#ff4d4f' }}>
                          上限を超えているため新規登録できません。
                          不要な独自情報を削除するか、プランをアップグレードしてください。
                        </div>
                      )}
                    </div>
                  }
                >
                  <div style={{ minWidth: 140, cursor: 'help' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: usageData.storage_exceeded
                            ? '#ff4d4f'
                            : '#595959',
                          fontWeight: usageData.storage_exceeded
                            ? 'bold'
                            : 'normal',
                        }}
                      >
                        容量 {usageData.storage_exceeded && '⚠'}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          color: usageData.storage_exceeded
                            ? '#ff4d4f'
                            : '#8c8c8c',
                        }}
                      >
                        {(usageData.storage_mb || 0).toFixed(1)} /{' '}
                        {usageData.max_storage_mb || 0} MB
                      </span>
                    </div>
                    <Progress
                      percent={
                        usageData.max_storage_mb > 0
                          ? Math.min(
                              100,
                              Math.round(
                                (usageData.storage_mb /
                                  usageData.max_storage_mb) *
                                  100,
                              ),
                            )
                          : 0
                      }
                      size="small"
                      strokeColor={
                        usageData.storage_exceeded
                          ? '#ff4d4f'
                          : {
                              '0%': '#9254de',
                              '100%': '#d3adf7',
                            }
                      }
                      showInfo={false}
                    />
                  </div>
                </Tooltip>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* TIPS（独自情報管理用） */}
      <div style={{ marginBottom: 16 }}>
        <TipsRotator tips={generalTips} intervalMs={7000} fadeMs={300} />
      </div>

      {/* 表示切替とアクション */}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Space>
          {/* 機能ロック状態の表示 */}
          {!featureStatus.unlocked &&
          userRole !== 'admin' &&
          userRole !== 'master' ? (
            <Tooltip
              title={`独自情報機能は${featureStatus.requiredPlan}プラン以上で利用可能です`}
            >
              <Button
                type="primary"
                icon={<LockOutlined />}
                disabled
                style={{ minWidth: '160px', fontWeight: 'bold' }}
              >
                ロック中
              </Button>
            </Tooltip>
          ) : featureStatus.currentCount >= featureStatus.maxLimit &&
            featureStatus.maxLimit > 0 &&
            userRole !== 'admin' &&
            userRole !== 'master' ? (
            <Tooltip
              title={`登録上限（${featureStatus.maxLimit}件）に達しました。上位プランで上限が増加します`}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled
                style={{ minWidth: '160px', fontWeight: 'bold' }}
              >
                上限到達
              </Button>
            </Tooltip>
          ) : (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setWizardVisible(true)}
              style={{ minWidth: '160px', fontWeight: 'bold' }}
            >
              新規追加
            </Button>
          )}
          <Button
            icon={<SearchOutlined />}
            onClick={() => setSearchModalVisible(true)}
          >
            高度な検索
          </Button>
          {(userRole === 'admin' || userRole === 'master') && (
            <Select
              allowClear
              showSearch
              placeholder="全てのユーザー"
              value={selectedUserId}
              onChange={setSelectedUserId}
              style={{ minWidth: 200 }}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={[
                { value: null, label: '全てのユーザー' },
                ...users.map(user => ({
                  value: user.user_id,
                  label: `${user.last_name}${user.first_name} (${user.email})${
                    user.company_name ? ` - ${user.company_name}` : ''
                  }`,
                })),
              ]}
            />
          )}
        </Space>

        <Segmented
          options={[
            { value: 'table', icon: <TableOutlined /> },
            { value: 'card', icon: <AppstoreOutlined /> },
          ]}
          value={viewMode}
          onChange={setViewMode}
        />
      </div>

      {/* 一括操作バー */}
      {selectedRowKeys.length > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            backgroundColor: '#f0f2ff',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text>
            <strong>{selectedRowKeys.length}</strong> 件選択中
          </Text>
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setBatchEditModalVisible(true);
                fetchAvailableTags();
              }}
            >
              一括編集
            </Button>
            <Popconfirm
              title={`選択した${selectedRowKeys.length}件の独自情報を削除しますか？`}
              onConfirm={handleBatchDelete}
              okText="削除"
              cancelText="キャンセル"
              okButtonProps={{ loading: batchEditLoading }}
            >
              <Button danger icon={<DeleteOutlined />}>
                一括削除
              </Button>
            </Popconfirm>
            <Button onClick={() => setSelectedRowKeys([])}>選択解除</Button>
          </Space>
        </div>
      )}

      {/* 検索結果表示 */}
      {searchResults && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={`検索結果: "${searchResults.query}" で ${searchResults.total} 件見つかりました`}
            type="info"
            closable
            onClose={() => setSearchResults(null)}
          />
        </div>
      )}

      {/* コンテンツ表示 */}
      {viewMode === 'table' ? (
        <Table
          className="v2-scrollbar"
          columns={columns}
          dataSource={searchResults ? searchResults.items : toppings}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          rowSelection={rowSelection}
          onRow={record => ({
            onClick: event => {
              // チェックボックスやアクションボタンのクリックでは詳細表示しない
              if (
                event.target.closest('.ant-checkbox-wrapper') ||
                event.target.closest('.ant-btn') ||
                event.target.closest('.ant-switch') ||
                event.target.closest('[data-action]')
              ) {
                return;
              }
              showDrawer(record);
            },
            style: { cursor: 'pointer' },
          })}
          pagination={{
            showSizeChanger: true,
            showTotal: total => `全 ${total} 件`,
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      ) : (
        renderCardView()
      )}

      {/* 高度な検索モーダル */}
      <Modal
        title="高度な検索"
        visible={searchModalVisible}
        onCancel={() => setSearchModalVisible(false)}
        width={600}
        footer={null}
      >
        <Form layout="vertical" onFinish={handleAdvancedSearch}>
          <Form.Item
            name="query"
            label="検索キーワード"
            rules={[
              { required: true, message: '検索キーワードを入力してください' },
            ]}
          >
            <Input placeholder="検索したいキーワードを入力" size="large" />
          </Form.Item>

          <Form.Item name="searchType" label="検索対象" initialValue="all">
            <Select>
              <Option value="all">すべて</Option>
              <Option value="title">タイトルのみ</Option>
              <Option value="content">コンテンツのみ</Option>
              <Option value="metadata">メタデータのみ</Option>
            </Select>
          </Form.Item>

          <Form.Item name="category" label="カテゴリー">
            <Select placeholder="選択してください" allowClear>
              <Option value="cta">CTA・行動喚起</Option>
              <Option value="product">商品・サービス情報</Option>
              <Option value="case_study">事例・実績</Option>
              <Option value="faq">よくある質問</Option>
              <Option value="statistics">統計・データ</Option>
              <Option value="testimonial">口コミ・評価</Option>
              <Option value="company">会社情報</Option>
              <Option value="support">サポート・問い合わせ</Option>
              <Option value="other">その他</Option>
            </Select>
          </Form.Item>

          <Form.Item name="tags" label="タグ">
            <Select mode="tags" placeholder="タグを入力（複数可）">
              <Option value="重要">重要</Option>
              <Option value="最新">最新</Option>
              <Option value="公式">公式</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setSearchModalVisible(false)}>
                キャンセル
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={searchLoading}
                icon={<SearchOutlined />}
              >
                検索実行
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 検索結果プレビュー */}
        {searchResults && searchResults.items.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Divider>検索結果プレビュー</Divider>
            <List
              dataSource={searchResults.items.slice(0, 5)}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    avatar={getTypeIcon(item.type)}
                    title={renderHighlightedText(
                      item.title,
                      item.title_highlighted,
                    )}
                    description={
                      <div>
                        {item.content_highlighted && (
                          <Paragraph
                            ellipsis={{ rows: 2 }}
                            style={{ marginBottom: 8 }}
                          >
                            {renderHighlightedText(
                              null,
                              item.content_highlighted,
                            )}
                          </Paragraph>
                        )}
                        <Space>
                          {item.category && <Tag>{item.category}</Tag>}
                          {item.tags.map(tag => (
                            <Tag key={tag} color="volcano">
                              {tag}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            {searchResults.total > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button
                  type="link"
                  onClick={() => {
                    setSearchModalVisible(false);
                    // 検索結果をメインテーブルに表示
                  }}
                >
                  すべての結果を表示（{searchResults.total}件）
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 追加/編集ドロワー */}
      <Drawer
        title={editingTopping ? '独自情報を編集' : '独自情報を追加'}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={900}
        placement="right"
        className="topping-edit-drawer"
        bodyStyle={{
          paddingBottom: 80,
        }}
        footer={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              style={{ flex: 1 }}
              onClick={() => {
                setDrawerVisible(false);
              }}
            >
              キャンセル
            </Button>
            <Button type="primary" style={{ flex: 1 }} onClick={handleSubmit}>
              {editingTopping ? '更新' : '追加'}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          {/* 基本情報 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="タイトル"
                rules={[
                  { required: true, message: 'タイトルを入力してください' },
                ]}
              >
                <Input placeholder="独自情報のタイトル" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label="カテゴリー">
                <Select placeholder="カテゴリーを選択">
                  <Option value="cta">CTA・行動喚起</Option>
                  <Option value="product">商品・サービス情報</Option>
                  <Option value="case_study">事例・実績</Option>
                  <Option value="faq">よくある質問</Option>
                  <Option value="statistics">統計・データ</Option>
                  <Option value="testimonial">口コミ・評価</Option>
                  <Option value="company">会社情報</Option>
                  <Option value="support">サポート・問い合わせ</Option>
                  <Option value="other">その他</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="説明">
            <TextArea rows={3} placeholder="独自情報の説明（任意）" />
          </Form.Item>

          <Form.Item name="tags" label="タグ">
            <Select mode="tags" placeholder="タグを入力（Enterで確定）">
              <Option value="重要">重要</Option>
              <Option value="最新">最新</Option>
              <Option value="公式">公式</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="ステータス"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="アクティブ" unCheckedChildren="無効" />
              </Form.Item>
            </Col>
          </Row>

          {/* ファイル再アップロード（編集時のみ、URLとテキスト以外） */}
          {editingTopping &&
            editingTopping.type !== 'text' &&
            editingTopping.type !== 'url' && (
              <Collapse
                style={{ marginTop: 16 }}
                ghost
                items={[
                  {
                    key: 'reupload',
                    label: (
                      <span style={{ fontWeight: 'bold' }}>
                        📁 ファイル再アップロード（任意）
                      </span>
                    ),
                    children: (
                      <div>
                        <Alert
                          message="ファイルを再アップロードすると、既存のコンテンツが置き換えられます"
                          type="warning"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                        <Form.Item name="file">
                          <Dragger
                            customRequest={handleUpload}
                            fileList={fileList}
                            onChange={({ fileList }) => setFileList(fileList)}
                            maxCount={1}
                            accept=".csv,.pdf,.txt,.html"
                          >
                            <p className="ant-upload-drag-icon">
                              <UploadOutlined />
                            </p>
                            <p className="ant-upload-text">
                              ファイルを再アップロードして内容を更新
                            </p>
                            <p className="ant-upload-hint">
                              CSV、PDF、テキスト、HTMLファイルに対応（最大10MB）
                            </p>
                          </Dragger>
                        </Form.Item>
                      </div>
                    ),
                  },
                ]}
              />
            )}

          {/* 新規追加時のアップロード方法選択 */}
          {!editingTopping && (
            <Form.Item label="追加方法">
              <Tabs activeKey={uploadType} onChange={setUploadType}>
                <TabPane tab="ファイルアップロード" key="file">
                  <Form.Item name="file">
                    <Dragger
                      customRequest={handleUpload}
                      fileList={fileList}
                      onChange={({ fileList }) => setFileList(fileList)}
                      maxCount={1}
                      accept=".csv,.pdf,.txt,.html"
                    >
                      <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        ファイルをドラッグ＆ドロップまたはクリックしてアップロード
                      </p>
                      <p className="ant-upload-hint">
                        CSV、PDF、テキスト、HTMLファイルに対応（最大10MB）
                      </p>
                    </Dragger>
                  </Form.Item>
                </TabPane>
                <TabPane tab="URL" key="url">
                  <Form.Item
                    name="url"
                    label="URL"
                    rules={[
                      { required: true, message: 'URLを入力してください' },
                      { type: 'url', message: '有効なURLを入力してください' },
                    ]}
                  >
                    <Input placeholder="https://example.com/page" />
                  </Form.Item>
                </TabPane>
                <TabPane tab="テキスト" key="text">
                  <Form.Item
                    name="content"
                    label="コンテンツ"
                    rules={[
                      { required: true, message: 'テキストを入力してください' },
                    ]}
                  >
                    <TextArea rows={8} placeholder="独自情報のテキストを入力" />
                  </Form.Item>
                </TabPane>
              </Tabs>
            </Form.Item>
          )}

          {/* RAG設定（URLとテキスト以外のみ） */}
          {(!editingTopping ||
            (editingTopping.type !== 'url' &&
              editingTopping.type !== 'text')) && (
            <>
              <Divider>RAG設定</Divider>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="rag_enabled"
                    label="RAG有効化"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Switch checkedChildren="有効" unCheckedChildren="無効" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="chunk_size"
                    label="チャンクサイズ"
                    initialValue={1000}
                  >
                    <InputNumber
                      min={100}
                      max={5000}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="chunk_overlap"
                    label="オーバーラップ"
                    initialValue={200}
                  >
                    <InputNumber min={0} max={1000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Drawer>

      {/* 詳細表示モーダル - 完全リニューアル版 */}
      <Modal
        title={null}
        visible={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        width={Math.min(1200, window.innerWidth * 0.9)}
        style={{ top: 20 }}
        bodyStyle={{
          padding: 0,
          maxHeight: window.innerHeight * 0.8,
          overflow: 'auto',
        }}
        footer={null}
        centered={false}
        closable={false}
      >
        {viewingTopping && (
          <div>
            {/* ヘッダー部分 */}
            <div
              style={{
                padding: '24px 24px 20px 24px',
                borderBottom: '1px solid #f0f0f0',
                background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                color: 'white',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: '24px',
                        marginRight: 12,
                        color: 'white',
                      }}
                    >
                      {viewingTopping.type === 'csv' ? (
                        <FileExcelOutlined style={{ color: 'white' }} />
                      ) : viewingTopping.type === 'pdf' ? (
                        <FilePdfOutlined style={{ color: 'white' }} />
                      ) : viewingTopping.type === 'url' ? (
                        <LinkOutlined style={{ color: 'white' }} />
                      ) : (
                        <FileTextOutlined style={{ color: 'white' }} />
                      )}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          opacity: 0.9,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                        }}
                      >
                        {viewingTopping.type === 'csv'
                          ? 'CSV データ'
                          : viewingTopping.type === 'pdf'
                            ? 'PDF ドキュメント'
                            : viewingTopping.type === 'url'
                              ? 'WEB ページ'
                              : 'テキスト データ'}
                      </div>
                      <Title
                        level={3}
                        style={{ color: 'white', margin: 0, fontSize: '20px' }}
                      >
                        {viewingTopping.title}
                      </Title>
                    </div>
                  </div>

                  {viewingTopping.description && (
                    <div
                      style={{ fontSize: '14px', opacity: 0.9, marginTop: 8 }}
                    >
                      {viewingTopping.description}
                    </div>
                  )}
                </div>

                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => setViewModalVisible(false)}
                  style={{ color: 'white', border: 'none' }}
                  size="large"
                />
              </div>

              {/* メタ情報 - グラデーション内に移動 */}
              <div style={{ marginTop: 20 }}>
                {/* 1段目: ステータス、カテゴリー、タグ */}
                <Row gutter={24} style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          opacity: 0.8,
                          marginBottom: 4,
                        }}
                      >
                        ステータス
                      </div>
                      <div>
                        {React.cloneElement(
                          getStatusTag(viewingTopping.status, viewingTopping),
                          {
                            style: {
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.3)',
                            },
                          },
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          opacity: 0.8,
                          marginBottom: 4,
                        }}
                      >
                        カテゴリー
                      </div>
                      <div>
                        {viewingTopping.category ? (
                          <Tag
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.3)',
                            }}
                          >
                            {(() => {
                              const categoryMap = {
                                'cta': 'CTA・行動喚起',
                                'product': '商品・サービス情報',
                                'case_study': '事例・実績',
                                'faq': 'よくある質問',
                                'statistics': '統計・データ',
                                'testimonial': '口コミ・評価',
                                'company': '会社情報',
                                'support': 'サポート・問い合わせ',
                                'other': 'その他',
                              };
                              return (
                                categoryMap[viewingTopping.category] ||
                                viewingTopping.category
                              );
                            })()}
                          </Tag>
                        ) : (
                          <span style={{ opacity: 0.7 }}>未分類</span>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          opacity: 0.8,
                          marginBottom: 4,
                        }}
                      >
                        タグ
                      </div>
                      <div>
                        {viewingTopping.tags?.length > 0 ? (
                          viewingTopping.tags.slice(0, 2).map(tag => (
                            <Tag
                              key={tag}
                              size="small"
                              style={{
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                border: '1px solid rgba(255,255,255,0.3)',
                                margin: '0 2px',
                              }}
                            >
                              {tag}
                            </Tag>
                          ))
                        ) : (
                          <span style={{ opacity: 0.7 }}>なし</span>
                        )}
                        {viewingTopping.tags?.length > 2 && (
                          <span style={{ fontSize: '12px', opacity: 0.7 }}>
                            +{viewingTopping.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* 2段目: ユニット、最終更新日 */}
                <Row gutter={24}>
                  <Col span={12}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          opacity: 0.8,
                          marginBottom: 4,
                        }}
                      >
                        消費ユニット
                      </div>
                      <div
                        style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#1890ff',
                        }}
                      >
                        {(() => {
                          const content = viewingTopping.content || '';
                          if (viewingTopping.type === 'csv') {
                            const lines = content
                              .split('\n')
                              .filter(line => line.trim() !== '');
                            const rowCount = Math.max(0, lines.length - 1);
                            return rowCount > 0 ? Math.ceil(rowCount / 100) : 1;
                          } else if (viewingTopping.type === 'pdf') {
                            const pageCount =
                              viewingTopping.structured_data?.pages || 0;
                            return pageCount > 0 ? Math.ceil(pageCount / 5) : 1;
                          } else if (viewingTopping.type === 'url') {
                            return 1;
                          } else {
                            const charCount = content.length;
                            return charCount > 0
                              ? Math.ceil(charCount / 5000)
                              : 1;
                          }
                        })()}
                      </div>
                      <div
                        style={{
                          fontSize: '11px',
                          color: '#8c8c8c',
                          marginTop: 2,
                        }}
                      >
                        {(() => {
                          const content = viewingTopping.content || '';
                          if (viewingTopping.type === 'csv') {
                            const lines = content
                              .split('\n')
                              .filter(line => line.trim() !== '');
                            const rowCount = Math.max(0, lines.length - 1);
                            return `${rowCount.toLocaleString()}行`;
                          } else if (viewingTopping.type === 'pdf') {
                            const pageCount =
                              viewingTopping.structured_data?.pages || 0;
                            return `${pageCount}ページ`;
                          } else if (viewingTopping.type === 'url') {
                            return 'URL';
                          } else {
                            return `${content.length.toLocaleString()}文字`;
                          }
                        })()}
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          opacity: 0.8,
                          marginBottom: 4,
                        }}
                      >
                        最終更新
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {new Date(
                          viewingTopping.updated_at ||
                            viewingTopping.created_at,
                        ).toLocaleDateString('ja-JP')}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>

            {/* ソースURL（存在する場合のみ） */}
            {viewingTopping.source_url && (
              <div
                style={{
                  padding: '12px 24px',
                  background: '#f9f9f9',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <div
                  style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}
                >
                  ソースURL
                </div>
                <div style={{ fontSize: '13px' }}>
                  <a
                    href={viewingTopping.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all' }}
                  >
                    {viewingTopping.source_url.length > 80
                      ? viewingTopping.source_url.substring(0, 80) + '...'
                      : viewingTopping.source_url}
                  </a>
                </div>
              </div>
            )}

            {/* コンテンツ表示部分 - タイプ別最適化 */}
            <div
              style={{
                padding: '24px',
                background: 'white',
              }}
            >
              {viewingTopping.type === 'csv' ? (
                // CSV専用レイアウト
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      padding: '12px 16px',
                      background: '#e6f7ff',
                      borderRadius: '6px',
                      border: '1px solid #91d5ff',
                    }}
                  >
                    <div>
                      <FileExcelOutlined
                        style={{
                          color: '#1890ff',
                          fontSize: '16px',
                          marginRight: 8,
                        }}
                      />
                      <Text strong>
                        {viewingTopping.file_name || 'CSVファイル'}
                      </Text>
                    </div>
                    <div>
                      {(() => {
                        const lines = viewingTopping.content
                          .split('\n')
                          .filter(line => line.trim() !== '');
                        const headers = lines[0]?.split(',').length || 0;
                        const dataRows = Math.max(0, lines.length - 1);
                        return (
                          <Text type="secondary">
                            {headers}列 × {dataRows.toLocaleString()}行
                          </Text>
                        );
                      })()}
                    </div>
                  </div>

                  <div
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '6px',
                      overflow: 'hidden',
                    }}
                  >
                    {(() => {
                      const lines = viewingTopping.content
                        .split('\n')
                        .filter(line => line.trim() !== '');
                      if (lines.length === 0)
                        return (
                          <div style={{ padding: 20, textAlign: 'center' }}>
                            データがありません
                          </div>
                        );

                      const headers = lines[0]
                        .split(',')
                        .map(header => header.trim().replace(/"/g, ''));
                      const previewColumns = headers.map((header, index) => ({
                        title: header,
                        dataIndex: `col_${index}`,
                        key: `col_${index}`,
                        width: 150,
                        ellipsis: true,
                      }));

                      const previewData = lines
                        .slice(1, 21)
                        .map((line, index) => {
                          const values = line
                            .split(',')
                            .map(value => value.trim().replace(/"/g, ''));
                          const record = { key: index.toString() };
                          headers.forEach((_, colIndex) => {
                            record[`col_${colIndex}`] = values[colIndex] || '';
                          });
                          return record;
                        });

                      return (
                        <div>
                          <Table
                            bordered
                            dataSource={previewData}
                            columns={previewColumns}
                            pagination={false}
                            scroll={{ x: 'max-content', y: 400 }}
                            size="small"
                          />
                          {lines.length > 21 && (
                            <div
                              style={{
                                textAlign: 'center',
                                padding: '12px',
                                background: '#fafafa',
                                borderTop: '1px solid #f0f0f0',
                              }}
                            >
                              <Text type="secondary">
                                ... 他 {lines.length - 21} 行（全{' '}
                                {lines.length - 1} 件のデータ）
                              </Text>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : viewingTopping.type === 'pdf' ? (
                // PDF専用レイアウト
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      padding: '12px 16px',
                      background: '#fff2e8',
                      borderRadius: '6px',
                      border: '1px solid #ffbb96',
                    }}
                  >
                    <div>
                      <FilePdfOutlined
                        style={{
                          color: '#fa541c',
                          fontSize: '16px',
                          marginRight: 8,
                        }}
                      />
                      <Text strong>
                        {viewingTopping.file_name || 'PDFファイル'}
                      </Text>
                    </div>
                    <div>
                      <Text type="secondary">
                        {viewingTopping.content?.length.toLocaleString() || 0}{' '}
                        文字
                      </Text>
                    </div>
                  </div>

                  <div
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '6px',
                      padding: '20px',
                      background: '#fafafa',
                      maxHeight: '500px',
                      overflow: 'auto',
                    }}
                  >
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        margin: 0,
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        lineHeight: '1.6',
                      }}
                    >
                      {viewingTopping.content
                        ? viewingTopping.content.length > 3000
                          ? viewingTopping.content.substring(0, 3000) +
                            '\n\n... (続きは省略されました)'
                          : viewingTopping.content
                        : 'PDFの内容を読み込み中...'}
                    </pre>
                  </div>
                </div>
              ) : viewingTopping.type === 'url' ? (
                // URL専用レイアウト
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      padding: '12px 16px',
                      background: '#f6ffed',
                      borderRadius: '6px',
                      border: '1px solid #b7eb8f',
                    }}
                  >
                    <div>
                      <LinkOutlined
                        style={{
                          color: '#52c41a',
                          fontSize: '16px',
                          marginRight: 8,
                        }}
                      />
                      <Text strong>WEBページ</Text>
                    </div>
                    <div>
                      <Text type="secondary">
                        {viewingTopping.content?.length.toLocaleString() || 0}{' '}
                        文字
                      </Text>
                    </div>
                  </div>

                  <div
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '6px',
                      padding: '20px',
                      background: '#fafafa',
                      maxHeight: '500px',
                      overflow: 'auto',
                    }}
                  >
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        margin: 0,
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        lineHeight: '1.6',
                      }}
                    >
                      {viewingTopping.content?.substring(0, 2000)}
                      {viewingTopping.content?.length > 2000 &&
                        '\n\n... (続きは省略されました)'}
                    </pre>
                  </div>
                </div>
              ) : (
                // テキスト専用レイアウト
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      padding: '12px 16px',
                      background: '#f9f0ff',
                      borderRadius: '6px',
                      border: '1px solid #d3adf7',
                    }}
                  >
                    <div>
                      <FileTextOutlined
                        style={{
                          color: '#722ed1',
                          fontSize: '16px',
                          marginRight: 8,
                        }}
                      />
                      <Text strong>テキストデータ</Text>
                    </div>
                    <div>
                      <Text type="secondary">
                        {viewingTopping.content?.length.toLocaleString() || 0}{' '}
                        文字
                      </Text>
                    </div>
                  </div>

                  <div
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '6px',
                      padding: '20px',
                      background: '#fafafa',
                      maxHeight: '500px',
                      overflow: 'auto',
                    }}
                  >
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        margin: 0,
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        lineHeight: '1.6',
                      }}
                    >
                      {viewingTopping.content?.substring(0, 2000)}
                      {viewingTopping.content?.length > 2000 &&
                        '\n\n... (続きは省略されました)'}
                    </pre>
                  </div>
                </div>
              )}

              {/* メタデータ（存在する場合のみ） */}
              {viewingTopping.content_metadata && (
                <div style={{ marginTop: 24 }}>
                  <Collapse
                    ghost
                    items={[
                      {
                        key: 'metadata',
                        label: (
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <InfoCircleOutlined style={{ marginRight: 8 }} />
                            <Text strong>メタデータ</Text>
                          </div>
                        ),
                        children: (
                          <pre
                            style={{
                              background: '#f5f5f5',
                              padding: 16,
                              borderRadius: 4,
                              fontSize: '12px',
                              maxHeight: '200px',
                              overflow: 'auto',
                            }}
                          >
                            {JSON.stringify(
                              viewingTopping.content_metadata,
                              null,
                              2,
                            )}
                          </pre>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* 新しいウィザード形式の追加画面 */}
      <AddToppingWizard
        visible={wizardVisible}
        onCancel={() => setWizardVisible(false)}
        onSuccess={() => {
          setWizardVisible(false);
          fetchToppings(); // リストを更新
        }}
      />

      {/* 新しいウィザード形式の編集画面 */}
      <EditToppingWizard
        visible={editWizardVisible}
        topping={editingToppingForWizard}
        onCancel={() => {
          setEditWizardVisible(false);
          setEditingToppingForWizard(null);
        }}
        onSuccess={() => {
          setEditWizardVisible(false);
          setEditingToppingForWizard(null);
          fetchToppings(); // リストを更新
        }}
      />

      {/* 一括編集モーダル */}
      <Modal
        title={`一括編集 (${selectedRowKeys.length}件選択中)`}
        visible={batchEditModalVisible}
        onCancel={() => {
          setBatchEditModalVisible(false);
          batchEditForm.resetFields();
        }}
        width={600}
        footer={null}
      >
        <Form form={batchEditForm} layout="vertical" onFinish={handleBatchEdit}>
          <Alert
            description="入力した項目のみが更新されます。空のまま送信した項目は変更されません。"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item name="status" label="ステータス">
            <Select placeholder="変更しない" allowClear>
              <Option value={true}>アクティブ</Option>
              <Option value={false}>無効</Option>
            </Select>
          </Form.Item>

          <Form.Item name="category" label="カテゴリー">
            <Select placeholder="変更しない" allowClear>
              <Option value="cta">CTA・行動喚起</Option>
              <Option value="product">商品・サービス情報</Option>
              <Option value="case_study">事例・実績</Option>
              <Option value="faq">よくある質問</Option>
              <Option value="statistics">統計・データ</Option>
              <Option value="testimonial">口コミ・評価</Option>
              <Option value="company">会社情報</Option>
              <Option value="support">サポート・問い合わせ</Option>
              <Option value="other">その他</Option>
            </Select>
          </Form.Item>

          <Form.Item name="tags" label="タグ">
            <Select
              mode="tags"
              placeholder="変更しない（新しいタグを追加する場合は入力）"
              allowClear
            >
              {availableTags.map(tag => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button
                onClick={() => {
                  setBatchEditModalVisible(false);
                  batchEditForm.resetFields();
                }}
              >
                キャンセル
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={batchEditLoading}
                icon={<SaveOutlined />}
              >
                一括更新
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ToppingManager;
